import { useEffect, useRef, useState } from 'react';
import databaseMonitorService, { ReplicationStatus } from '../../../../../services/databaseMonitor.service';
import { Button } from '../ui';

const INTERVALS = [2000, 3000, 5000, 10000];

export function ReplicationView({ serverId }: { serverId: string }) {
  const [data, setData] = useState<ReplicationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pollMs, setPollMs] = useState(3000);
  const [isFetching, setIsFetching] = useState(false);
  const [dataUpdatedAt, setDataUpdatedAt] = useState<number>(0);
  const [refreshTick, setRefreshTick] = useState(0);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    abortRef.current?.abort();
    if (timerRef.current) clearTimeout(timerRef.current);

    let cancelled = false;
    const controller = new AbortController();
    abortRef.current = controller;
    let first = data == null;
    if (first) setLoading(true);

    const run = async () => {
      if (document.hidden) {
        if (cancelled || controller.signal.aborted) return;
        timerRef.current = setTimeout(run, pollMs);
        return;
      }
      setIsFetching(true);
      try {
        const d = await databaseMonitorService.getReplication(serverId, controller.signal);
        if (controller.signal.aborted) return;
        setData(d);
        setError(null);
        setDataUpdatedAt(Date.now());
      } catch (err: any) {
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || controller.signal.aborted) return;
        setError(err?.response?.data?.detail ?? err?.message ?? 'Failed to load replication status');
      } finally {
        if (!controller.signal.aborted) {
          setIsFetching(false);
          if (first) {
            setLoading(false);
            first = false;
          }
        }
      }
      if (cancelled || controller.signal.aborted) return;
      timerRef.current = setTimeout(run, pollMs);
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [serverId, pollMs, refreshTick]);

  if (loading) return <div className="text-slate-600 dark:text-slate-400">Loading…</div>;
  if (error) return <div className="text-red-600 dark:text-red-400">Error: {error}</div>;
  if (!data) return null;

  if (!data.is_replica) {
    return <div className="text-slate-600 dark:text-slate-400">Not a replica.</div>;
  }

  const ioRunning = data.raw['Slave_IO_Running'] ?? data.raw['Replica_IO_Running'] ?? null;
  const sqlRunning = data.raw['Slave_SQL_Running'] ?? data.raw['Replica_SQL_Running'] ?? null;
  const secondsBehindRaw =
    data.raw['Seconds_Behind_Master'] ?? data.raw['Seconds_Behind_Source'] ?? null;

  const entries = Object.entries(data.raw).sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-end gap-3 text-sm flex-wrap">
        <label className="text-slate-600 dark:text-slate-400 flex items-center gap-2">
          Refresh
          <select
            className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md px-2 py-1 text-sm text-slate-900 dark:text-slate-100"
            value={pollMs}
            onChange={(e) => setPollMs(Number(e.target.value))}
          >
            {INTERVALS.map((ms) => (
              <option key={ms} value={ms}>
                {ms / 1000}s
              </option>
            ))}
          </select>
        </label>
        <Button variant="secondary" onClick={() => setRefreshTick((t) => t + 1)}>
          Refresh now
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatusCard label="Slave_IO_Running" value={ioRunning} kind="running" />
        <StatusCard label="Slave_SQL_Running" value={sqlRunning} kind="running" />
        <StatusCard label="Seconds_Behind_Master" value={secondsBehindRaw} kind="lag" />
      </div>

      <div className="border border-slate-200 dark:border-slate-800 rounded-md">
        <table className="min-w-full text-xs">
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} className="border-b border-slate-100 dark:border-slate-900">
                <td className="px-3 py-1 text-slate-600 dark:text-slate-400 whitespace-nowrap">{k}</td>
                <td className="px-3 py-1 break-all text-slate-900 dark:text-slate-100">{v ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-500">
        {isFetching ? 'Refreshing…' : dataUpdatedAt ? `Updated ${relative(dataUpdatedAt)}` : ''}
      </div>
    </div>
  );
}

type CardKind = 'running' | 'lag';

function StatusCard({
  label,
  value,
  kind,
}: {
  label: string;
  value: string | null;
  kind: CardKind;
}) {
  const tone = toneFor(kind, value);
  return (
    <div
      className={
        'rounded-md border px-4 py-3 flex flex-col gap-1 ' +
        tone.container
      }
    >
      <div className={'text-xs font-medium ' + tone.label}>{label}</div>
      <div className={'text-2xl font-semibold ' + tone.value}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function toneFor(kind: CardKind, value: string | null): { container: string; label: string; value: string } {
  if (value == null) {
    return {
      container: 'bg-slate-50 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800',
      label: 'text-slate-600 dark:text-slate-400',
      value: 'text-slate-900 dark:text-slate-100',
    };
  }
  if (kind === 'running') {
    const isYes = value.toLowerCase() === 'yes';
    if (isYes) {
      return {
        container: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800',
        label: 'text-emerald-700 dark:text-emerald-300',
        value: 'text-emerald-700 dark:text-emerald-300',
      };
    }
    return {
      container: 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800',
      label: 'text-red-700 dark:text-red-300',
      value: 'text-red-700 dark:text-red-300',
    };
  }
  const n = Number(value);
  const isNum = Number.isFinite(n);
  let palette: { container: string; label: string; value: string };
  if (!isNum) {
    palette = {
      container: 'bg-slate-50 dark:bg-slate-900/60 border-slate-200 dark:border-slate-800',
      label: 'text-slate-600 dark:text-slate-400',
      value: 'text-slate-900 dark:text-slate-100',
    };
  } else if (n >= 60) {
    palette = {
      container: 'bg-red-50 dark:bg-red-950/40 border-red-300 dark:border-red-800',
      label: 'text-red-700 dark:text-red-300',
      value: 'text-red-700 dark:text-red-300',
    };
  } else if (n >= 10) {
    palette = {
      container: 'bg-amber-50 dark:bg-amber-950/40 border-amber-300 dark:border-amber-800',
      label: 'text-amber-700 dark:text-amber-300',
      value: 'text-amber-700 dark:text-amber-300',
    };
  } else {
    palette = {
      container: 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-300 dark:border-emerald-800',
      label: 'text-emerald-700 dark:text-emerald-300',
      value: 'text-emerald-700 dark:text-emerald-300',
    };
  }
  return palette;
}

function relative(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 1000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  return `${Math.floor(diff / 60_000)}m ago`;
}

export default ReplicationView;
